/** Side of the repeating fog picture in pixels; the pattern wraps seamlessly at its edges. */
export const FOG_TEXTURE_SIZE = 256;

/** Wave counts per texture side; whole numbers keep the pattern seamless when it repeats. */
const WAVES: readonly { fx: number; fy: number; phase: number; weight: number }[] = [
  { fx: 1, fy: 2, phase: 0.3, weight: 1 },
  { fx: 2, fy: 1, phase: 1.7, weight: 0.9 },
  { fx: 3, fy: 2, phase: 4.1, weight: 0.7 },
  { fx: 2, fy: 4, phase: 2.2, weight: 0.5 },
  { fx: 5, fy: 3, phase: 5.5, weight: 0.4 },
  { fx: 4, fy: 6, phase: 0.9, weight: 0.3 },
];

/** Cloudiness 0..1 at a point of the repeating fog picture. */
export function fogDensity(x: number, y: number, size = FOG_TEXTURE_SIZE): number {
  let sum = 0;
  let total = 0;
  for (const wave of WAVES) {
    const angle = 2 * Math.PI * ((wave.fx * x) / size + (wave.fy * y) / size) + wave.phase;
    sum += Math.sin(angle) * wave.weight;
    total += wave.weight;
  }
  // Sum of sines sits near 0 with most of its spread within ±0.5·total; stretch to 0..1.
  return Math.min(1, Math.max(0, 0.5 + sum / (total * 1.1)));
}

/**
 * RGBA pixels for one layer of fog: thin, pale mist where the density is low and darker, bluish
 * shadow where it is high, so the veil reads as shadowy and light at once.
 */
export function fogPixels(size = FOG_TEXTURE_SIZE, maxAlpha = 0.7): Uint8ClampedArray {
  const pixels = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const d = fogDensity(x, y, size);
      const shade = d * d * (3 - 2 * d); // smoothstep: patches with soft rims
      const i = (y * size + x) * 4;
      pixels[i] = 205 - 95 * shade;
      pixels[i + 1] = 210 - 95 * shade;
      pixels[i + 2] = 230 - 80 * shade;
      pixels[i + 3] = 255 * maxAlpha * (0.25 + 0.75 * shade);
    }
  }
  return pixels;
}
